import { Printer } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import { getCurrentPage } from "~/functions";
import { useSelector } from "react-redux";
import api from "~/services/api";
import { toast } from "react-toastify";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";
import RateChart from "../Surveys/RateChart";

function getCurrentMonthRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  const pad = (value) => String(value).padStart(2, "0");
  const toISO = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return { from: toISO(from), to: toISO(to) };
}

export default function EnrollmentSurveys() {
  const defaultPeriod = getCurrentMonthRange();
  const [surveys, setSurveys] = useState([]);
  const [enrollmentFrom, setEnrollmentFrom] = useState(defaultPeriod.from);
  const [enrollmentTo, setEnrollmentTo] = useState(defaultPeriod.to);
  const [selectedProcessType, setSelectedProcessType] = useState("all");
  const [selectedProcessSubStatus, setSelectedProcessSubStatus] =
    useState("all");
  const [processTypes, setProcessTypes] = useState([]);
  const [processSubStatuses, setProcessSubStatuses] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [viewMode, setViewMode] = useState("charts");
  const [textAnswers, setTextAnswers] = useState([]);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);

  const filtersDisabled = !enrollmentFrom || !enrollmentTo;

  const currentPage = getCurrentPage();
  const { accesses } = useSelector((state) => state.auth);
  const accessModule = accesses.hierarchy.find(
    (el) => el.alias === "administrative"
  );

  // Substatuses are filtered on the client by the selected process type
  const visibleSubStatuses = useMemo(() => {
    if (selectedProcessType === "all") return processSubStatuses;
    return processSubStatuses.filter(
      (sub) => String(sub.processtype_id) === selectedProcessType
    );
  }, [processSubStatuses, selectedProcessType]);

  function handleClearFilters() {
    setSelectedProcessType("all");
    setSelectedProcessSubStatus("all");
    const range = getCurrentMonthRange();
    setEnrollmentFrom(range.from);
    setEnrollmentTo(range.to);
    setExpandedQuestionId(null);
  }

  function buildParams() {
    const params = {};
    if (enrollmentFrom && enrollmentTo) {
      params.enrollment_from = enrollmentFrom;
      params.enrollment_to = enrollmentTo;
    }
    if (selectedProcessType !== "all") params.processtype = selectedProcessType;
    if (selectedProcessSubStatus !== "all")
      params.processsubstatus = selectedProcessSubStatus;
    if (viewMode === "text") params.text_answer = "yes";
    return params;
  }

  async function loadData() {
    try {
      const { data } = await api.get("/surveyenrollment-answers", {
        params: buildParams(),
      });
      if (viewMode === "text") {
        setTextAnswers(data?.rows || []);
        setSurveys([]);
      } else {
        setSurveys(data?.rows || []);
        setTextAnswers([]);
      }
    } catch (error) {
      console.error("Error fetching enrollment survey answers:", error);
      setSurveys([]);
      setTextAnswers([]);
    }
  }

  async function fetchAvailableFilters() {
    setLoadingFilters(true);
    try {
      const { data } = await api.get(
        "/surveyenrollment-answers/available-filters"
      );
      const types = data?.processtypes || [];
      const subs = data?.processsubstatuses || [];
      setProcessTypes(types);
      setProcessSubStatuses(subs);

      if (
        selectedProcessType !== "all" &&
        !types.some((t) => String(t.id) === selectedProcessType)
      ) {
        setSelectedProcessType("all");
      }
      if (
        selectedProcessSubStatus !== "all" &&
        !subs.some((s) => String(s.id) === selectedProcessSubStatus)
      ) {
        setSelectedProcessSubStatus("all");
      }
    } catch (error) {
      console.error("Error fetching available filters:", error);
      setProcessTypes([]);
      setProcessSubStatuses([]);
    } finally {
      setLoadingFilters(false);
    }
  }

  const handlePrint = async () => {
    if (filtersDisabled) return;
    setGeneratingPdf(true);
    try {
      const params = new URLSearchParams();
      params.set("enrollment_from", enrollmentFrom);
      params.set("enrollment_to", enrollmentTo);
      if (selectedProcessType !== "all")
        params.set("processtype", selectedProcessType);
      if (selectedProcessSubStatus !== "all")
        params.set("processsubstatus", selectedProcessSubStatus);

      const response = await api.get(
        `/surveyenrollment-answers/pdf?${params.toString()}`,
        { responseType: "blob" }
      );

      const filename =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"]
        ) || `enrollment_surveys_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  useEffect(() => {
    fetchAvailableFilters();
  }, []);

  // When the process type changes, drop a substatus that no longer belongs to it
  useEffect(() => {
    if (
      selectedProcessSubStatus !== "all" &&
      !visibleSubStatuses.some(
        (sub) => String(sub.id) === selectedProcessSubStatus
      )
    ) {
      setSelectedProcessSubStatus("all");
    }
  }, [selectedProcessType, visibleSubStatuses, selectedProcessSubStatus]);

  useEffect(() => {
    loadData();
  }, [
    enrollmentFrom,
    enrollmentTo,
    selectedProcessType,
    selectedProcessSubStatus,
    viewMode,
  ]);

  const textQuestions = useMemo(() => {
    if (!textAnswers.length) return [];
    const map = new Map();
    textAnswers.forEach((row) => {
      const key = row.question_id ?? row.question ?? row.id;
      if (!map.has(key)) {
        map.set(key, {
          question_id: row.question_id ?? row.id,
          question: row.question,
          answers: [],
        });
      }
      map.get(key).answers.push(row);
    });
    return Array.from(map.values()).map((item) => ({
      ...item,
      answers: item.answers.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date > b.date ? -1 : 1;
      }),
    }));
  }, [textAnswers]);

  const selectedQuestion = useMemo(() => {
    return (
      textQuestions.find(
        (question) => question.question_id === expandedQuestionId
      ) ||
      textQuestions[0] ||
      null
    );
  }, [expandedQuestionId, textQuestions]);

  useEffect(() => {
    if (viewMode !== "text") return;
    if (expandedQuestionId !== null) return;
    if (!textQuestions.length) return;
    setExpandedQuestionId(textQuestions[0].question_id);
  }, [expandedQuestionId, textQuestions, viewMode]);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "surveys-enrollment"
  );

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar>
          <button
            type="button"
            onClick={handlePrint}
            disabled={filtersDisabled || generatingPdf}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            <Printer size={14} />
            {generatingPdf ? "Generating..." : "Print"}
          </button>
        </FiltersBar>
      </PageHeader>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">
            Enrollment from / to
          </label>
          <div className="flex gap-2">
            <div className="flex flex-col gap-1">
              <div className="px-2 text-xs">From</div>
              <input
                type="date"
                value={enrollmentFrom}
                onChange={(e) => setEnrollmentFrom(e.target.value)}
                className="w-32 border rounded-xs p-2 px-4 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="px-2 text-xs">To</div>
              <input
                type="date"
                value={enrollmentTo}
                onChange={(e) => setEnrollmentTo(e.target.value)}
                className="w-32 border rounded-xs p-2 px-4 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">
            Process Type
          </label>
          <select
            value={selectedProcessType}
            onChange={(e) => setSelectedProcessType(e.target.value)}
            disabled={loadingFilters || filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Process Types</option>
            {processTypes.map((type) => (
              <option key={type.id} value={String(type.id)}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">
            Process Sub Status
          </label>
          <select
            value={selectedProcessSubStatus}
            onChange={(e) => setSelectedProcessSubStatus(e.target.value)}
            disabled={loadingFilters || filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Sub Statuses</option>
            {visibleSubStatuses.map((sub) => (
              <option key={sub.id} value={String(sub.id)}>
                {sub.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">View</label>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            disabled={filtersDisabled}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="charts">Charts</option>
            <option value="text">Text Answers</option>
          </select>
        </div>

        <button
          type="button"
          onClick={handleClearFilters}
          disabled={loadingFilters}
          className="text-xs border rounded-sm px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:border-gray-400 disabled:opacity-50"
        >
          Clear filters
        </button>
        <div className="flex-1" />
        <div className="text-xs text-gray-500">
          <p>
            {filtersDisabled
              ? "Select a period to enable filters"
              : "Filters update automatically"}
          </p>
        </div>
      </div>

      <div className="flex flex-1 w-full p-4">
        {viewMode === "text" ? (
          <div className="flex gap-4 w-full">
            <div
              className="w-80 flex-shrink-0 overflow-y-auto space-y-2 bg-gray-50 p-2 rounded-lg"
              style={{ maxHeight: "calc(100vh - 300px)" }}
            >
              {textQuestions.map((question) => {
                const isSelected =
                  expandedQuestionId === question.question_id;
                return (
                  <button
                    key={question.question_id}
                    onClick={() => setExpandedQuestionId(question.question_id)}
                    className={`w-full text-left border rounded-lg px-4 py-3 shadow-sm transition-colors ${
                      isSelected
                        ? "bg-mila_orange text-white border-mila_orange"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="text-sm font-medium line-clamp-2">
                      {question.question}
                    </div>
                    <div className="text-xs mt-1 opacity-75">
                      {question.answers.length} answer(s)
                    </div>
                  </button>
                );
              })}
            </div>

            <div
              className="flex-1 flex flex-col bg-white border rounded-lg shadow-sm"
              style={{ maxHeight: "calc(100vh - 300px)" }}
            >
              <div className="border-b px-6 py-4 bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedQuestion?.question || "Select a question"}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedQuestion
                    ? `${selectedQuestion.answers.length} answer(s)`
                    : "Click a question to see answers"}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {selectedQuestion ? (
                  <div className="space-y-4">
                    {selectedQuestion.answers.map((answer, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                      >
                        <div className="text-sm text-gray-900 mb-2">
                          {answer.answer_description || answer.answer}
                        </div>
                        <div className="text-xs text-gray-500 flex flex-row gap-2">
                          {answer.student_name && (
                            <span>{answer.student_name}</span>
                          )}
                          {answer.date && <span>{answer.date}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No question selected.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 w-full flex flex-col pt-6 overflow-y-auto">
            <div className="min-w-[1200px] w-full overflow-x-auto">
              <div className="grid grid-cols-2 gap-4">
                {surveys.map((survey, index) => {
                  if (survey.type === "Text Area") {
                    return null;
                  }
                  return <RateChart key={index} survey={survey} />;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
